/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Node Registry
   Central registration catalog for extensible node definitions.
   ═══════════════════════════════════════════════════════════════ */

import type { NodeInstance, PortInstance, Position } from '../domain/types';
import { generateStableId } from '../domain/id';
import type { NodeDefinition } from './types';

// Inputs
import { colorInputNode } from './inputs/colorInput';
import { numberInputNode } from './inputs/numberInput';
import { imageInputNode } from './inputs/imageInput';

// Math
import { mathAddNode } from './math/mathAdd';

// Transforms
import { resizeNode } from './transform/resize';
import { cropNode } from './transform/crop';
import { rotateNode } from './transform/rotate';
import { flipNode } from './transform/flip';
import { transform2dNode } from './transform/transform2d';

// Color & Tone
import { brightnessContrastNode } from './color/brightnessContrast';
import { saturationNode } from './color/saturation';
import { levelsNode } from './color/levels';
import { thresholdNode } from './color/threshold';
import { posterizeNode } from './color/posterize';
import { invertNode } from './color/invert';
import { hsvNode } from './color/hsv';
import { colorMixNode } from './color/colorMix';

// Filters & Creative Effects
import { gaussianBlurNode } from './filter/blur';
import { sharpenNode } from './filter/sharpen';
import { halftoneNode } from './filter/halftone';
import { ditherNode } from './filter/dither';
import { grainNode } from './filter/grain';
import { pixelateNode } from './filter/pixelate';
import { rgbSplitNode } from './filter/rgbSplit';
import { edgeDetectNode } from './filter/edgeDetect';

// Compositing & Masking
import { blendNode } from './composite/blend';
import { maskNode } from './composite/mask';

// Output
import { outputPreviewNode } from './output/outputPreview';

export class NodeRegistry {
  private definitions = new Map<string, NodeDefinition>();

  constructor() {
    // Inputs
    this.register(imageInputNode);
    this.register(colorInputNode);
    this.register(numberInputNode);

    // Math
    this.register(mathAddNode);

    // Transform
    this.register(resizeNode);
    this.register(cropNode);
    this.register(rotateNode);
    this.register(flipNode);
    this.register(transform2dNode);

    // Color
    this.register(brightnessContrastNode);
    this.register(saturationNode);
    this.register(levelsNode);
    this.register(thresholdNode);
    this.register(posterizeNode);
    this.register(invertNode);
    this.register(hsvNode);
    this.register(colorMixNode);

    // Filter
    this.register(gaussianBlurNode);
    this.register(sharpenNode);
    this.register(halftoneNode);
    this.register(ditherNode);
    this.register(grainNode);
    this.register(pixelateNode);
    this.register(rgbSplitNode);
    this.register(edgeDetectNode);

    // Composite
    this.register(blendNode);
    this.register(maskNode);

    // Output
    this.register(outputPreviewNode);
  }

  register(definition: NodeDefinition): void {
    this.definitions.set(definition.type, definition);
  }

  get(type: string): NodeDefinition | undefined {
    return this.definitions.get(type);
  }

  getAll(): NodeDefinition[] {
    return Array.from(this.definitions.values());
  }

  getByCategory(): Record<string, NodeDefinition[]> {
    const categories: Record<string, NodeDefinition[]> = {};
    for (const def of this.definitions.values()) {
      if (!categories[def.category]) {
        categories[def.category] = [];
      }
      categories[def.category].push(def);
    }
    return categories;
  }

  createDefaultInstance(
    type: string,
    position: Position,
    explicitId?: string
  ): NodeInstance | null {
    const def = this.get(type);
    if (!def) return null;

    const id = explicitId || generateStableId('node');

    const inputs: Record<string, PortInstance> = {};
    for (const p of def.inputs) {
      inputs[p.id] = {
        id: p.id,
        name: p.name,
        type: p.type,
        direction: 'input',
        defaultValue: p.defaultValue,
        multi: p.multi,
      };
    }

    const outputs: Record<string, PortInstance> = {};
    for (const p of def.outputs) {
      outputs[p.id] = {
        id: p.id,
        name: p.name,
        type: p.type,
        direction: 'output',
        defaultValue: p.defaultValue,
      };
    }

    const params: Record<string, unknown> = {};
    for (const p of def.params) {
      params[p.id] = p.defaultValue;
    }

    return {
      id,
      type: def.type,
      version: def.version || 1,
      position,
      size: { width: 220, height: 120 },
      collapsed: false,
      inputs,
      outputs,
      params,
      meta: {
        label: def.label,
      },
    };
  }

  migrateNodeInstance(node: NodeInstance): NodeInstance {
    const def = this.get(node.type);
    if (!def) return node;

    const targetVersion = def.version || 1;
    const currentVersion = node.version || 1;

    if (currentVersion < targetVersion && def.migrateParams) {
      const updatedParams = def.migrateParams(node.params, currentVersion, targetVersion);
      return {
        ...node,
        version: targetVersion,
        params: updatedParams,
      };
    }

    return {
      ...node,
      version: targetVersion,
    };
  }
}

export const defaultNodeRegistry = new NodeRegistry();
